import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CONFIG, isEntryEditable } from '@h2h/shared';
import { Placeholder } from '../../../components/ui/Placeholder';
import { Swords } from '../../../components/ui/icons';
import { useLeagueRounds, useRoundFixtures, useMyEntry } from '../../../hooks/useQueries';
import { useHaptics } from '../../../hooks/useHaptics';
import { buildDuelVMs, duelsResolved } from '../../../lib/cyclingRound';
import { LiveDuelSheet } from '../../match-detail/LiveDuelSheet';
import { stagePhaseTone } from './helpers';
import { useNow } from '../helpers';
import { StageDuelList } from './StageDuelList';
import { StagePanel } from './StagePanel';
import { StageStakeLadder } from './StageStakeLadder';
import type { StageCtaKind } from './stageUserContext';

const PICKS_REQUIRED = CONFIG.CYCLING.DUELS_PER_STAGE;

/**
 * Cycling stage history — summary panel + hub-style ladder when fully entered.
 */
export function StagesTab({
  leagueId,
  currentRoundId,
  uid
}: {
  leagueId: string;
  currentRoundId: string | null;
  uid: string | null;
}) {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const now = useNow(true);
  const [params, setParams] = useSearchParams();
  const roundParam = params.get('round');
  const { data: rounds } = useLeagueRounds(leagueId);

  const ordered = useMemo(
    () => [...(rounds ?? [])].sort((a, b) => a.firstKickoffAt - b.firstKickoffAt),
    [rounds]
  );
  const initialIdx = useMemo(() => {
    const target = roundParam ?? currentRoundId;
    const i = ordered.findIndex((r) => r.roundId === target);
    return i >= 0 ? i : Math.max(0, ordered.length - 1);
  }, [ordered, currentRoundId, roundParam]);

  const [idx, setIdx] = useState<number | null>(null);
  const [liveDuelId, setLiveDuelId] = useState<string | null>(null);
  const activeIdx = idx ?? initialIdx;
  const selected = ordered[activeIdx];
  const selectedRoundId = selected?.roundId ?? currentRoundId;
  const isCurrentStage = selectedRoundId === currentRoundId;
  const entryEditable = selected ? isEntryEditable(selected, [], now) : false;

  const live =
    selected?.status === 'OPEN' || selected?.status === 'LOCKING' || selected?.status === 'SETTLING';
  const stageLive = stagePhaseTone(selected?.status) === 'live' || stagePhaseTone(selected?.status) === 'settling';

  const { data: fixtures } = useRoundFixtures(leagueId, selectedRoundId, live);
  const { data: entry } = useMyEntry(leagueId, selectedRoundId, uid);

  const fixtureMap = useMemo(
    () => new Map((fixtures ?? []).map((f) => [f.fixtureId, f])),
    [fixtures]
  );
  const duels = useMemo(
    () => (entry?.duelPicks?.length ? buildDuelVMs(entry, fixtureMap) : []),
    [entry, fixtureMap]
  );

  const pickCount = entry?.duelPicks?.length ?? 0;
  const hasFullLadder = pickCount >= PICKS_REQUIRED;
  const roundScore = entry?.roundScore;
  const resolvedDuels = duelsResolved(fixtureMap);
  const totalDuels = fixtures?.length ?? PICKS_REQUIRED;

  const recap = useMemo(() => {
    if (!hasFullLadder) return null;
    let won = 0;
    let resolved = 0;
    let pending = 0;
    let points = 0;
    for (const d of duels) {
      if (d.status === 'settled') {
        resolved++;
        if (d.correct) won++;
      } else if (d.status === 'upcoming') {
        pending++;
      }
      points += d.points ?? 0;
    }
    return { won, resolved, points, pending, sweep: pending === 0 && resolved > 0 && won === resolved };
  }, [duels, hasFullLadder]);

  const stageFixtures = useMemo(
    () => [...(fixtures ?? [])].sort((a, b) => a.fixtureId.localeCompare(b.fixtureId)),
    [fixtures]
  );

  const openDuel = (duelId: string) => {
    impact('light');
    if (selected) setLiveDuelId(duelId);
  };

  const handleCta = (kind: StageCtaKind) => {
    impact('medium');
    if (kind === 'compete') {
      setParams((p) => {
        p.delete('tab');
        return p;
      }, { replace: true });
      return;
    }
    const q = selectedRoundId && selectedRoundId !== currentRoundId ? `?round=${selectedRoundId}` : '';
    navigate(`/play/${leagueId}${q}`);
  };

  const selectRound = (roundId: string) => {
    impact('selection');
    const i = ordered.findIndex((r) => r.roundId === roundId);
    if (i >= 0) setIdx(i);
  };

  return (
    <div className="md:mx-auto md:w-full md:max-w-3xl">
      <StagePanel
        rounds={ordered}
        selectedRoundId={selectedRoundId}
        currentRoundId={currentRoundId}
        round={selected}
        isCurrentStage={isCurrentStage}
        pickCount={pickCount}
        picksRequired={PICKS_REQUIRED}
        resolvedCount={resolvedDuels}
        totalDuels={totalDuels}
        roundScore={roundScore}
        recap={recap}
        stageLive={stageLive}
        entryEditable={entryEditable}
        onSelectRound={selectRound}
        onCta={handleCta}
      />

      {hasFullLadder && entry ? (
        <StageStakeLadder entry={entry} fixtures={stageFixtures} stageLive={stageLive} onOpenDuel={openDuel} />
      ) : pickCount > 0 || stageFixtures.length > 0 ? (
        <StageDuelList duels={duels} fixtures={stageFixtures} stageLive={stageLive} onOpenDuel={openDuel} />
      ) : (
        <Placeholder
          Icon={Swords}
          title="No duels yet"
          body="The duel slate for this stage hasn't been drawn. Check back once the start list lands."
        />
      )}

      {selected && (
        <LiveDuelSheet
          openDuelId={liveDuelId}
          onClose={() => setLiveDuelId(null)}
          leagueId={leagueId}
          round={selected}
          fixtures={stageFixtures}
          entry={entry}
        />
      )}
    </div>
  );
}
